import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { showRealtimeBrowserNotification } from './browserNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    // WLAB hotfix 20260907.1 (stats refetch storm): see fetchConversationStats.
    this.statsRefreshTimer = null;
    this.lastStatsEmittedAt = 0;
    window.__WLAB_STATS_THROTTLE = '20260907.1';
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  accountIdFromPath = () => {
    try {
      const parts = window.location.pathname.split('/');
      // /app/accounts/:id/...
      const id = parts?.[3];
      return id ? Number(id) : null;
    } catch (e) {
      return null;
    }
  };

  currentAccountIdSafe = () => {
    const id =
      this.app?.$store?.getters?.getCurrentAccountId ??
      this.app?.$store?.getters?.getCurrentAccount?.id ??
      this.accountIdFromPath();
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  isAValidEvent = data => {
    // Some payloads may not include account_id; don't drop them.
    if (!data || data.account_id == null) return true;

    const current = this.currentAccountIdSafe();
    const incoming = Number(data.account_id);

    // If we can't determine current account yet, don't drop events.
    if (!current || !Number.isFinite(incoming)) return true;

    return current === incoming;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
    // Keep chat list timestamp in sync when message status/source updates
    try {
      const {
        conversation: { last_activity_at: lastActivityAt } = {},
        conversation_id: conversationId,
      } = data || {};
      if (conversationId && lastActivityAt) {
        this.app.$store.dispatch('updateConversationLastActivity', {
          lastActivityAt,
          conversationId,
        });
      }
    } catch (e) {
      // no-op: defensive guard
    }
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
    // Sync accepts_chat in real-time so TeamManagement status updates instantly
    if (data.user_accepts_chat) {
      this.app.$store.dispatch(
        'agents/updateAcceptsChat',
        data.user_accepts_chat
      );
    }
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    DashboardAudioNotificationHelper.onConversationCreated(data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
    emitter.emit(BUS_EVENTS.REFRESH_SIDEBAR_UNREAD_COUNTS);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    DashboardAudioNotificationHelper.onConversationStatusChange(data);
    this.fetchConversationStats();
    emitter.emit(BUS_EVENTS.REFRESH_SIDEBAR_UNREAD_COUNTS);
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = payload => {
    const { conversation, user } = payload || {};
    const conversationId = conversation?.id;
    if (!conversationId || !user) return;
    const typingPreview =
      payload?.typing_preview ??
      payload?.typingPreview ??
      payload?.typing_content ??
      '';

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
      typingPreview,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // WLAB hotfix 20260907.1: previously every conversation.* broadcast emitted
  // 'fetch_conversation_stats' immediately, so every open dashboard tab hit
  // GET /conversations/meta at the same instant. Fleet-wide that produced
  // hundreds of synchronized requests per minute, saturating web threads in
  // bursts; ActionCable pings then arrived later than the client's 6s stale
  // threshold, causing the mass disconnect/reconnect ("keeps showing offline",
  // flickering conversation list, delayed incoming chats) reported 2026-09-07.
  // Coalesce all triggers into at most one emit per 30s window, with per-tab
  // random jitter so tabs never fire in lockstep. Trailing emit guarantees the
  // counts still refresh after the last event.
  fetchConversationStats = () => {
    if (this.statsRefreshTimer) return;
    const STATS_MIN_INTERVAL_MS = 30000;
    const jitterMs = 1000 + Math.floor(Math.random() * 4000);
    const elapsedMs = Date.now() - this.lastStatsEmittedAt;
    const waitMs = Math.max(
      jitterMs,
      STATS_MIN_INTERVAL_MS - elapsedMs + jitterMs
    );
    this.statsRefreshTimer = setTimeout(() => {
      this.statsRefreshTimer = null;
      this.lastStatsEmittedAt = Date.now();
      emitter.emit('fetch_conversation_stats');
    }, waitMs);
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    DashboardAudioNotificationHelper.onNotificationCreated(data.notification);
    showRealtimeBrowserNotification(data.notification);
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
